import React, {
  PropsWithChildren, useCallback, useEffect, useRef,
} from 'react';
import cx from 'classnames';
import BSDropdown from 'react-bootstrap/Dropdown';
import {Tooltip} from '../Tooltip/Tooltip';
import {Inputfield} from '../Inputfield/Inputfield';
import SearchableDropdownToggle from './SearchableDropdownToggle';
import './Dropdown.scss';

export interface DropdownItem {
  id?: string;
  name: string;
  code?: string;
  description?: string;
}

export type DropdownProps = {
  id: string;
  items: DropdownItem[];
  selectedItem: string;
  defaultItem?: string;
  placeholder?: string;
  emptyMessage?: string;
  custom?: string | null;
  onChange?: (id: string) => void;
  onCustom?: (text: string | null) => void;
  className?: string;
  disabled?: boolean;
  testId?: string;
  endAdornment?: React.ReactNode;
  isFlex?: boolean;
  searchable?: boolean;
  customMaxLength?: number;
  customAllowSpecialCharacters?: boolean;
};

function DropdownItem({id, name, description}: PropsWithChildren<DropdownItem>) {
  const parentRef = useRef(null);

  return (
    <BSDropdown.Item eventKey={id} data-testid={id} ref={parentRef} id={`dropdown-item-${id}`}>
      <span className='dropdown-menu-text'>{name}</span>
      {description
        ? (
          <Tooltip id={id!} parentRef={parentRef} className='dropdown-tooltip'>{description}</Tooltip>
        )
        : null}
    </BSDropdown.Item>
  );
}

const EMPTY_ID = '@@Dropdown/Empty';
export const CUSTOM_ID = '@@Dropdown/Custom';

export function Dropdown({
  id,
  items,
  selectedItem,
  defaultItem,
  custom,
  onChange = () => {},
  onCustom,
  className = '',
  placeholder = 'Select option',
  emptyMessage = 'No options available',
  disabled = false,
  testId = 'dropdown',
  endAdornment,
  isFlex,
  searchable = false,
  customMaxLength = undefined,
  customAllowSpecialCharacters = true,
}: DropdownProps) {
  const [filter, setFilter] = React.useState<string>('');
  const [open, setOpen] = React.useState<boolean>(false);
  items = onCustom ? [...items, {id: CUSTOM_ID, name: 'Custom'}] : items;
  const displayItem = items.find(item => item.id === selectedItem)
    ?? (items.length === 0 ? {name: emptyMessage} : {name: placeholder});
  items = items.length === 0 ? [{id: EMPTY_ID, name: emptyMessage}] : items;

  const filteredItems = filter === ''
    ? items
    : items.filter(i => {
      const searchValue = filter.toLowerCase();
      return i.name.toLowerCase().includes(searchValue) || (i.code?.toLowerCase().includes(searchValue));
    });

  const handleChange = useCallback(id => {
    if (id !== EMPTY_ID) {
      onChange(id ?? defaultItem ?? '');

      if (id === CUSTOM_ID) {
        onCustom?.('');
      }
    }
  }, [onChange, onCustom, defaultItem]);

  useEffect(() => {
    if (!open) {
      setFilter('');
    }
  }, [open]);

  const removeCustom = useCallback(() => {
    onChange(defaultItem ?? '');
    onCustom?.(null);
  }, [onChange, onCustom, defaultItem]);

  if (selectedItem === CUSTOM_ID) {
    return (
      <Inputfield
        id={id}
        autoFocus
        value={custom ?? undefined}
        iconType='undo'
        placeholder='Custom'
        canClickIcon
        onClickIcon={removeCustom}
        onChange={value => onCustom?.(value)}
        maxLength={customMaxLength}
        allowSpecialCharacters={customAllowSpecialCharacters}
      />
    );
  }

  const SearchableMenu = React.forwardRef((props, ref) => (
    <SearchableDropdownToggle
      ref={ref}
      value={filter}
      onChange={setFilter}
      placeholder={displayItem.code ?? displayItem.name}
      {...props}
    />
  ));

  return (
    <div className={cx('qep-dropdown', className)} style={{flex: isFlex ? 1 : ''}}>
      <BSDropdown onSelect={handleChange} data-testid={testId} onToggle={show => {
        setOpen(show);
      }}>
        {open
          ? searchable
            ? (
              <BSDropdown.Toggle bsPrefix='no-carrot' as={SearchableMenu}/>
            )
            : (
              <BSDropdown.Toggle id={id} disabled={disabled}>
                <span className='dropdown-display-text'>
                  {displayItem.code ? displayItem.code : displayItem.name ?? ''}
                </span>
                {endAdornment && <span className='end-adornment'>
                  {endAdornment}
                </span>}
              </BSDropdown.Toggle>
            )
          : (
            <BSDropdown.Toggle id={id} disabled={disabled}>
              <span className='dropdown-display-text'>
                {displayItem.code ? displayItem.code : displayItem.name ?? ''}
              </span>
              {endAdornment && <span className='end-adornment'>
                {endAdornment}
              </span>}
            </BSDropdown.Toggle>
          )}

        <BSDropdown.Menu className={searchable ? 'fixed' : ''}>
          {filteredItems.map(item => (<DropdownItem key={item.id} {...item} />))}
        </BSDropdown.Menu>
      </BSDropdown>
    </div>
  );
}
